import { useEffect, useRef, useState } from "react";

export default function RemoteCarousel({ slides, interval = 4500 }) {
  const total = slides.length;

  const [active, setActive] = useState(0);     // can go to total (clone)
  const [paused, setPaused] = useState(false);
  const [transition, setTransition] = useState(true);

  const isSnappingRef = useRef(false);

  const next = () => setActive((p) => p + 1);

  const prev = () => {
    if (active === 0) return;
    setActive((p) => p - 1);
  };

  // autoplay
  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, interval);
    return () => clearInterval(id);
  }, [paused, interval]);

  // dots active index
  const dotActive = active >= total ? 0 : active;

  // when we reach clone, mark that we need to snap after transition ends
  useEffect(() => {
    if (active === total) {
      isSnappingRef.current = true;
    }
  }, [active, total]);

  const handleTransitionEnd = (e) => {
    // ensure we only react to the track transform transition
    if (e.propertyName !== "transform") return;

    if (isSnappingRef.current) {
      // turn off animation, snap to first real slide
      setTransition(false);
      setActive(0);
      isSnappingRef.current = false;

      // re-enable animation in the next frame so future slides animate
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTransition(true));
      });
    }
  };

  return (
    <section
      className="remoteCarousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      <div className="remotetitle">
        <h2>
          Proven Results: Enterprise Use Cases <br />
          Driving Business Value
        </h2>
      </div>

      <button className="rcNav rcPrev" onClick={prev} aria-label="Previous slide">
        ‹
      </button>

      <div className="rcViewport">
        <div
          className={`rcTrack ${transition ? "rcAnimate" : ""}`}
          style={{ transform: `translateX(-${active * 100}%)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          {slides.map((s, idx) => (
            <div className="rcSlide" key={idx} aria-hidden={dotActive !== idx}>
              <div className="remoteWrap">
                <div className="remoteLeft">
                  <div className="remoteImgBox">
                    <img src={s.image} alt={s.title || `Slide ${idx + 1}`} />
                  </div>
                </div>

                <div className="remoteRight">
                  <div className="remoteFrame">
                    <div className="remoteInner">
                      <h2 className="remoteTitle">
                        {(s.title || "").split("\n").map((line, i) => (
                          <span key={i}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </h2>
                      <p className="remoteDesc">{s.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* clone first slide */}
          {total > 0 && (
            <div className="rcSlide" aria-hidden="true">
              <div className="remoteWrap">
                <div className="remoteLeft">
                  <div className="remoteImgBox">
                    <img src={slides[0].image} alt={slides[0].title || "Slide 1"} />
                  </div>
                </div>

                <div className="remoteRight">
                  <div className="remoteFrame">
                    <div className="remoteInner">
                      <h2 className="remoteTitle">
                        {(slides[0].title || "").split("\n").map((line, i) => (
                          <span key={i}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </h2>
                      <p className="remoteDesc">{slides[0].desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <button className="rcNav rcNext" onClick={next} aria-label="Next slide">
        ›
      </button>

      <div className="rcDots" role="tablist" aria-label="Carousel pagination">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`rcDot ${dotActive === i ? "active" : ""}`}
            onClick={() => setActive(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-selected={dotActive === i}
            role="tab"
          />
        ))}
      </div>
    </section>
  );
}
